import { put, select, takeLatest } from "redux-saga/effects";

import { requestMiddleware } from "helpers/api";
import {
  getMissions,
  getMission,
  addMission,
  getVehicles,
  removeMission,
  putMission,
  startMissionAnalysis
} from "services/api/missions";
import { actions } from "./ducks";
import features from "features";
import { MapModes, UnitTypes } from "constant";
import { getProjectUnits } from "services/api/units";

function* fetchMissions({ payload }) {
  const request = getMissions;
  const {
    fetchMissionsSuccess: setSuccess,
    fetchMissionsError: setError,
    fetchMissionRequest
  } = actions;

  function* onSuccess(res) {
    if (res?.length > 0) {
      const newPayload = {
        params: { ...payload.params, missionId: res[0].id }
      } as any;
      yield put(fetchMissionRequest(newPayload));
    }
  }

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError,
    onSuccess
  });
}

function* fetchMission({ payload }) {
  const request = getMission;
  const { fetchMissionSuccess: setSuccess, fetchMissionError: setError } =
    actions;

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError
  });
}

function* createMission({ payload }) {
  const request = addMission;
  const { createMissionSuccess: setSuccess, createMissionError: setError } =
    actions;

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError
  });
}
function* preparePoints({ payload }) {
  const request = getProjectUnits;

  function* onSuccess(res) {
    yield updateMissionPoints({ payload: { ...payload, fields: res.units } });
  }

  yield requestMiddleware({
    ...payload,
    request,
    onSuccess
  });
}

function* runRouteAnalysis({ payload }) {
  yield preparePoints({
    payload: {
      params: { projectId: payload.params.projectId, type: UnitTypes.POINT }
    }
  });

  const request = startMissionAnalysis;
  const {
    runRouteAnalysisSuccess: setSuccess,
    runRouteAnalysisError: setError
  } = actions;

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError
  });
}

function* deleteMission({ payload }) {
  const request = removeMission;
  const {
    deleteMissionSuccess: setSuccess,
    deleteMissionError: setError,
    fetchMissionRequest,
    clearMission
  } = actions;

  function* onSuccess() {
    const { missions } = yield select((state) => state.missionsManager);

    if (missions.length > 0) {
      yield put(
        fetchMissionRequest({
          params: {
            projectId: payload.params.projectId,
            missionId: missions[missions.length - 1].id
          }
        } as any)
      );
    } else {
      yield put(clearMission());
    }
  }

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError,
    onSuccess
  });
}

function* fetchVehicles({ payload }) {
  const request = getVehicles;
  const { fetchVehiclesSuccess: setSuccess, fetchVehiclesError: setError } =
    actions;

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError
  });
}

function* updateMissionPoints({ payload }) {
  const request = putMission;
  const { createVehicleSuccess: setSuccess, createVehicleError: setError } =
    actions;

  const { mission } = yield select((state) => state.missionsManager);

  const newPayload = {
    params: { ...payload.params, missionId: mission.id },
    fields: {
      ...mission,
      data: {
        ...mission.data,
        obstacles: payload.fields.map((item) => {
          return {
            radius: item.additional.object_diameter ?? 1500,
            geo_data: item.geo_data
          };
        })
      }
    }
  };

  yield requestMiddleware({
    ...(newPayload as any),
    request,
    setSuccess,
    setError
  });
}

function* createVehicle({ payload }) {
  const request = putMission;
  const { createVehicleSuccess: setSuccess, createVehicleError: setError } =
    actions;

  const { mission } = yield select((state) => state.missionsManager);

  const newPayload = {
    params: { ...payload.params, missionId: mission.id },
    fields: {
      ...mission,
      data: {
        ...mission.data,
        vehicles: [
          ...mission.data.vehicles,
          {
            ...payload.val,
            head_width: 1200,
            id: 1
          }
        ]
      }
    }
  };

  yield requestMiddleware({
    ...(newPayload as any),
    request,
    setSuccess,
    setError
  });
}

function* updateVehicle({ payload }) {
  const request = putMission;
  const { updateVehicleSuccess: setSuccess, updateVehicleError: setError } =
    actions;

  const { mission, vehicle } = yield select((state) => state.missionsManager);

  const newPayload = {
    params: { ...payload.params, missionId: mission.id },
    fields: {
      ...mission,
      data: {
        ...mission.data,
        vehicles: mission.data.vehicles.map((item, i) =>
          i === vehicle.vehicleIndex ? { ...item, ...payload.val } : item
        )
      }
    }
  };

  yield requestMiddleware({
    ...(newPayload as any),
    request,
    setSuccess,
    setError
  });
}
function* deleteVehicle({ payload }) {
  const request = putMission;
  const { deleteVehicleSuccess: setSuccess, deleteVehicleError: setError } =
    actions;

  const { mission } = yield select((state) => state.missionsManager);

  const newPayload = {
    params: { ...payload.params, missionId: mission.id },
    fields: {
      ...mission,
      data: {
        ...mission.data,
        vehicles: mission.data.vehicles.filter(
          (_, index) => index !== payload.params.vehicleIndex
        )
      }
    }
  };

  yield requestMiddleware({
    ...(newPayload as any),
    request,
    setSuccess,
    setError
  });
}

function* initVehiclePositionAwaiting() {
  yield put(
    features.map.actions.setMapMode(MapModes.MISSION_VEHICLE_POINT_CREATION)
  );
}

export default function* watchSaga() {
  yield takeLatest(actions.fetchMissionsRequest, fetchMissions);
  yield takeLatest(actions.fetchMissionRequest, fetchMission);
  yield takeLatest(actions.deleteMissionRequest, deleteMission);
  yield takeLatest(actions.createMissionRequest, createMission);
  yield takeLatest(actions.runRouteAnalysisRequest, runRouteAnalysis);
  yield takeLatest(actions.fetchVehiclesRequest, fetchVehicles);
  yield takeLatest(actions.createVehicleRequest, createVehicle);
  yield takeLatest(actions.updateVehicleRequest, updateVehicle);
  yield takeLatest(actions.deleteVehicleRequest, deleteVehicle);
  yield takeLatest(actions.awaitVehiclePosition, initVehiclePositionAwaiting);
}
